// Portfolio/Developer Loan Calculator Logic
// Based on VASTER Term Sheet - 7/1 ARM 30 YR AMORTIZATION template

// Settlement Charges Fee Rates (from legend)
export const SETTLEMENT_FEE_RATES = {
  LENDERS_TITLE_POLICY: 0.00081,    // 0.081% of loan
  OWNERS_TITLE_INSURANCE: 0.0042,   // 0.42% of sales price
  DOCUMENTARY_STAMPS: 0.0035,       // 0.35% of loan
  INTANGIBLE_STAMPS: 0.002,         // 0.20% of loan
};

// Fixed Settlement Charges
export const FIXED_SETTLEMENT_FEES = {
  COURIER_EXPRESS_MAIL: 35,
  TITLE_SEARCH: 275,
  TITLE_ENDORSEMENTS: 375,
  LIEN_SEARCH: 200,
};

// Lender Fee Rates
export const LENDER_FEE_RATES = {
  ORIGINATION_POINTS: 0.0075,       // 0.75% default
};

/**
 * Calculate Loan Amount from Purchase Price and LTV
 * = Purchase Price × (LTV / 100)
 */
export const calculateLoanFromLTV = (purchasePrice, ltv) => {
  const price = parseFloat(purchasePrice) || 0;
  const ltvPercent = parseFloat(ltv) || 0;
  return price * (ltvPercent / 100);
};

/**
 * Calculate Interest-Only Monthly Payment
 * = (Loan Amount × Annual Interest Rate) / 12
 */
export const calculateIOPayment = (loanAmount, interestRate) => {
  const loan = parseFloat(loanAmount) || 0;
  const rate = parseFloat(interestRate) / 100 || 0;
  return (loan * rate) / 12;
};

/**
 * Calculate P&I (Principal & Interest) Monthly Payment
 * Standard amortization formula
 * M = P * [i(1+i)^n] / [(1+i)^n - 1]
 */
export const calculatePIPayment = (loanAmount, interestRate, termYears) => {
  const P = parseFloat(loanAmount) || 0;
  const annualRate = parseFloat(interestRate) / 100 || 0;
  const n = (parseFloat(termYears) || 30) * 12;
  
  if (P === 0 || annualRate === 0) return 0;
  
  const i = annualRate / 12; // Monthly interest rate
  const numerator = i * Math.pow(1 + i, n);
  const denominator = Math.pow(1 + i, n) - 1;
  
  return P * (numerator / denominator);
};

/**
 * Calculate Origination Fee
 * = Loan Amount × Origination Points
 */
export const calculateOriginationFee = (loanAmount, originationPoints) => {
  const points = parseFloat(originationPoints) / 100 || LENDER_FEE_RATES.ORIGINATION_POINTS;
  return (parseFloat(loanAmount) || 0) * points;
};

/**
 * Calculate Deposit Amount
 * = Purchase Price × Deposit Percentage
 */
export const calculateDeposit = (purchasePrice, depositPercent) => {
  const price = parseFloat(purchasePrice) || 0;
  const pct = parseFloat(depositPercent) / 100 || 0;
  return price * pct;
};

/**
 * Calculate Total Credits
 * = Developer Credit + Lender Credit
 */
export const calculateTotalCredits = (developerCredit, lenderCredit) => {
  return (parseFloat(developerCredit) || 0) + (parseFloat(lenderCredit) || 0);
};

/**
 * Calculate Developer Fee
 * = Purchase Price × Developer Fee Percentage
 */
export const calculateDeveloperFee = (purchasePrice, feePercentage) => {
  const price = parseFloat(purchasePrice) || 0;
  const pct = parseFloat(feePercentage) / 100 || 0;
  return price * pct;
};

/**
 * Calculate Capital Contribution
 * = Monthly HOA × Number of Months
 */
export const calculateCapitalContribution = (monthlyHOA, months) => {
  const hoa = parseFloat(monthlyHOA) || 0;
  const numMonths = parseFloat(months) || 0;
  return hoa * numMonths;
};

// Settlement Charges Calculations
/**
 * Calculate Lenders Title Policy
 * = Loan Amount × 0.081%
 */
export const calculateLendersTitlePolicy = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * SETTLEMENT_FEE_RATES.LENDERS_TITLE_POLICY;
};

/**
 * Calculate Owners Title Insurance
 * = Sales Price × 0.42%
 */
export const calculateOwnersTitleInsurance = (salesPrice) => {
  return (parseFloat(salesPrice) || 0) * SETTLEMENT_FEE_RATES.OWNERS_TITLE_INSURANCE;
};

/**
 * Calculate Documentary Stamps
 * = Loan Amount × 0.35%
 */
export const calculateDocStamps = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * SETTLEMENT_FEE_RATES.DOCUMENTARY_STAMPS;
};

/**
 * Calculate Intangible Stamps
 * = Loan Amount × 0.20%
 */
export const calculateIntangibleStamps = (loanAmount) => {
  return (parseFloat(loanAmount) || 0) * SETTLEMENT_FEE_RATES.INTANGIBLE_STAMPS;
};

/**
 * Calculate Escrow Tax
 * = (Annual Taxes / 12) × Escrow Months
 */
export const calculateEscrowTax = (annualTaxes, months) => {
  const taxes = parseFloat(annualTaxes) || 0;
  const escrowMonths = parseFloat(months) || 3;
  return (taxes / 12) * escrowMonths;
};

/**
 * Calculate Escrow Insurance
 * = (Annual Insurance / 12) × Escrow Months
 */
export const calculateEscrowInsurance = (annualInsurance, months) => {
  const insurance = parseFloat(annualInsurance) || 0;
  const escrowMonths = parseFloat(months) || 6;
  return (insurance / 12) * escrowMonths;
};

/**
 * Calculate Pre-Paid Interest
 * = (Monthly Interest Payment) × (Days / 30)
 */
export const calculatePrePaidInterest = (monthlyPayment, days) => {
  return (parseFloat(monthlyPayment) || 0) * (parseFloat(days) || 15) / 30;
};

/**
 * Calculate Total Monthly Housing Payment (PITI + HOA)
 * = P&I + Monthly Taxes + Monthly Insurance + HOA
 */
export const calculateTotalMonthlyPayment = (piPayment, annualTaxes, annualInsurance, monthlyHOA) => {
  const pi = parseFloat(piPayment) || 0;
  const monthlyTaxes = (parseFloat(annualTaxes) || 0) / 12;
  const monthlyInsurance = (parseFloat(annualInsurance) || 0) / 12;
  const hoa = parseFloat(monthlyHOA) || 0;
  
  return pi + monthlyTaxes + monthlyInsurance + hoa;
};

/**
 * Main calculation function for Portfolio/Developer Loan
 * LTV is an INPUT - Loan Amount is calculated from Purchase Price × LTV
 */
export const calculatePortfolioMetrics = (inputs) => {
  // Parse inputs - Purchase Price based (no appraised value at quote)
  const purchasePrice = parseFloat(inputs.purchasePrice) || 0;
  const targetLTV = parseFloat(inputs.targetLTV) || 75;
  const interestRate = parseFloat(inputs.interestRate) || 0;
  const amortizationTerm = parseFloat(inputs.amortizationTerm) || 30;
  
  // Calculate Loan Amount from Purchase Price and LTV
  const loanAmount = calculateLoanFromLTV(purchasePrice, targetLTV);
  
  // Monthly costs
  const annualTaxes = parseFloat(inputs.taxes) || 0;
  const annualInsurance = parseFloat(inputs.insurance) || 0;
  const monthlyHOA = parseFloat(inputs.hoa) || 0;
  
  // Lender fees inputs
  const originationPoints = parseFloat(inputs.originationPoints) || 0.75;
  const appraisalFee = parseFloat(inputs.appraisalFee) || 3500;
  const processingFee = parseFloat(inputs.processingFee) || 750;
  
  // Developer costs inputs
  const developerFeePercent = parseFloat(inputs.developerFeePercent) || 0;
  const capitalContributionMonths = parseFloat(inputs.capitalContributionMonths) || 0;
  
  // Deposit & Credits inputs
  const depositPercent = parseFloat(inputs.depositPercent) || 0;
  const developerCredit = parseFloat(inputs.developerCredit) || 0;
  const lenderCredit = parseFloat(inputs.lenderCredit) || 0;
  
  // Refinance-specific inputs
  const existingMortgageBalance = parseFloat(inputs.existingMortgageBalance) || 0;
  const transactionType = inputs.transactionType || '';
  
  // Pre-paids
  const escrowTaxMonths = parseFloat(inputs.escrowTaxMonths) || 3;
  const escrowInsuranceMonths = parseFloat(inputs.escrowInsuranceMonths) || 6;
  const prePaidInterestDays = parseFloat(inputs.prePaidInterestDays) || 15;
  
  // Calculate payments
  const ioPayment = calculateIOPayment(loanAmount, interestRate);
  const piPayment = calculatePIPayment(loanAmount, interestRate, amortizationTerm);
  
  // Calculate total monthly payment (PITI + HOA)
  const totalMonthlyPayment = calculateTotalMonthlyPayment(piPayment, annualTaxes, annualInsurance, monthlyHOA);
  
  // Calculate lender fees
  const originationFee = calculateOriginationFee(loanAmount, originationPoints);
  
  // Total lender fees
  const totalLenderFees = originationFee + appraisalFee + processingFee;
  
  // Calculate developer costs
  const developerFee = calculateDeveloperFee(purchasePrice, developerFeePercent);
  const capitalContribution = calculateCapitalContribution(monthlyHOA, capitalContributionMonths);
  
  // Total developer costs (Deed Tax removed - included in developer fees)
  const totalDeveloperCosts = developerFee + capitalContribution;
  
  // Calculate Settlement Charges (auto-calculated based on legend)
  const lendersTitlePolicy = calculateLendersTitlePolicy(loanAmount);
  const ownersTitleInsurance = calculateOwnersTitleInsurance(purchasePrice);
  const courierExpressMail = FIXED_SETTLEMENT_FEES.COURIER_EXPRESS_MAIL;
  const titleSearch = FIXED_SETTLEMENT_FEES.TITLE_SEARCH;
  const titleEndorsements = FIXED_SETTLEMENT_FEES.TITLE_ENDORSEMENTS;
  const docStamps = calculateDocStamps(loanAmount);
  const intangibleStamps = calculateIntangibleStamps(loanAmount);
  const lienSearch = FIXED_SETTLEMENT_FEES.LIEN_SEARCH;
  
  // Total Settlement Charges
  const totalSettlementCharges = lendersTitlePolicy + ownersTitleInsurance + courierExpressMail +
                                  titleSearch + titleEndorsements + docStamps + intangibleStamps + lienSearch;
  
  // Calculate pre-paids
  const escrowTax = calculateEscrowTax(annualTaxes, escrowTaxMonths);
  const escrowInsurance = calculateEscrowInsurance(annualInsurance, escrowInsuranceMonths);
  const prePaidInterest = calculatePrePaidInterest(ioPayment, prePaidInterestDays);
  const annualInsurancePremium = annualInsurance; // First year insurance
  
  // Total pre-paids
  const totalPrePaids = escrowTax + escrowInsurance + prePaidInterest + annualInsurancePremium;
  
  // Total closing costs = Lender Fees + Developer Costs + Settlement Charges + Pre-Paids
  const totalClosingCosts = totalLenderFees + totalDeveloperCosts + totalSettlementCharges + totalPrePaids;
  
  // Down payment = Purchase Price - Loan Amount
  const downPayment = purchasePrice - loanAmount;
  
  // Calculate Deposit (buyer funds already deposited, usually 40% of purchase price)
  const depositAmount = calculateDeposit(purchasePrice, depositPercent);
  
  // Calculate Total Credits
  const totalCredits = calculateTotalCredits(developerCredit, lenderCredit);
  
  // Cash to close = Down Payment + Total Closing Costs - Deposit - Credits
  // Can be negative if deposit + credits exceed down payment + closing costs (borrower gets refund)
  const cashToClose = downPayment + totalClosingCosts - depositAmount - totalCredits;
  
  // Cash Out Amount (for Refinance Cash-Out only)
  // = Loan Amount - Existing Mortgage Balance - Closing Costs
  const cashOutAmount = transactionType === 'refinance_cashout' 
    ? Math.max(loanAmount - existingMortgageBalance - totalClosingCosts, 0)
    : 0;
  
  // Sources = Loan Amount + Cash to Close + Deposit + Credits (= Total Uses)
  // When cashToClose is negative, it means excess funds go back to borrower
  const totalSources = loanAmount + Math.max(cashToClose, 0) + depositAmount + totalCredits;
  
  // Uses = Purchase Price + Total Closing Costs
  const totalUses = purchasePrice + totalClosingCosts;
  
  return {
    // Key metrics
    purchasePrice,
    loanAmount,
    targetLTV,
    interestRate,
    amortizationTerm,
    
    // Payment calculations
    ioPayment,
    piPayment,
    totalMonthlyPayment,
    
    // Monthly breakdown
    monthlyTaxes: annualTaxes / 12,
    monthlyInsurance: annualInsurance / 12,
    monthlyHOA,
    
    // Lender fees breakdown
    originationFee,
    originationPoints,
    appraisalFee,
    processingFee,
    totalLenderFees,
    
    // Developer costs breakdown
    developerFee,
    developerFeePercent,
    capitalContribution,
    capitalContributionMonths,
    totalDeveloperCosts,
    
    // Settlement Charges breakdown (auto-calculated)
    lendersTitlePolicy,
    ownersTitleInsurance,
    courierExpressMail,
    titleSearch,
    titleEndorsements,
    docStamps,
    intangibleStamps,
    lienSearch,
    totalSettlementCharges,
    
    // Pre-paids breakdown
    escrowTax,
    escrowTaxMonths,
    escrowInsurance,
    escrowInsuranceMonths,
    prePaidInterest,
    prePaidInterestDays,
    annualInsurancePremium,
    totalPrePaids,
    
    // Totals
    totalClosingCosts,
    
    // Deposit & Credits
    depositPercent,
    depositAmount,
    developerCredit,
    lenderCredit,
    totalCredits,
    
    // Refinance-specific
    existingMortgageBalance,
    cashOutAmount,
    transactionType,
    
    // Sources and Uses
    totalSources,
    totalUses,
    cashToClose,
    downPayment,
  };
};
